/**
 * webLeads.js — shared definitions for the Inquiries inbox (web leads from
 * rotoflightmedia.com).
 *
 * A "web lead" is a job created by the marketing site's public endpoints:
 *   source 'web-order'   → /real-estate/order booking request (POST /api/intake)
 *   source 'web-inquiry' → video/photography/TV inquiry form  (POST /api/inquiry)
 *
 * Triage state lives in jobs.triaged_at (camelCase triagedAt client-side):
 *   NULL = untouched. Set when Cyprian qualifies or dismisses the lead from
 *   the Inquiries page. A lead whose status advanced past 'intake' by any
 *   other path (e.g. quoted directly from the pipeline) also stops counting
 *   as "needs triage", so the badge can never get stuck.
 *
 * Used by InquiriesPage, the Sidebar badge, and the JobsPage entry banner —
 * keep the definitions here so all three always agree.
 */

export const WEB_LEAD_SOURCES = ['web-order', 'web-inquiry'];

/**
 * The void_reason the Inquiries page writes on Dismiss. Dismissed-ness is
 * keyed on this exact reason so a web lead that was QUALIFIED and later
 * voided in the pipeline (with a business reason) does NOT show up in the
 * Dismissed tab, where Restore would wrongly resurrect it to intake.
 */
export const DISMISS_REASON = 'Dismissed from inquiries';

export function isWebLead(job) {
  return WEB_LEAD_SOURCES.includes(job?.source);
}

/** Untouched new lead: still at intake and never triaged. Drives the badge. */
export function needsTriage(job) {
  return isWebLead(job) && job.status === 'intake' && !job.triagedAt;
}

/**
 * Dismissed from THIS inbox (see DISMISS_REASON). Includes leads the jobs
 * list has since auto-archived (void older than archiveVoidDays keeps its
 * void_reason through the sweep), so Dismissed history survives and Restore
 * keeps working past the archive window.
 */
export function isDismissed(job) {
  return (
    isWebLead(job) &&
    job.voidReason === DISMISS_REASON &&
    (job.status === 'void' || job.status === 'archived')
  );
}

/**
 * Qualified or otherwise progressed: anything not new and not dismissed.
 * Excluded on purpose: void leads (dismissed here, or voided in the pipeline
 * where that lifecycle is owned) and archived leads (aged out of the inbox
 * once the pipeline archives them — this is a triage inbox, not an archive
 * browser).
 */
export function isHandled(job) {
  return isWebLead(job) && !needsTriage(job) && job.status !== 'void' && job.status !== 'archived';
}

export function untriagedCount(jobs = []) {
  return jobs.reduce((n, j) => n + (needsTriage(j) ? 1 : 0), 0);
}

/** Split all jobs into the three inbox tabs, newest first within each. */
export function partitionWebLeads(jobs = []) {
  const byNewest = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  const leads = jobs.filter(isWebLead).sort(byNewest);
  return {
    needsTriage: leads.filter(needsTriage),
    handled: leads.filter(isHandled),
    dismissed: leads.filter(isDismissed),
  };
}

/** Chip label + color for the lead's channel. */
export function channelInfo(job) {
  if (job?.source === 'web-order') {
    return { label: 'Booking request', color: '#13B5EA' };
  }
  // web-inquiry jobs carry the practice in type: Video / Photography / TV.
  const practice = job?.jobType || job?.type || 'Project';
  return { label: `${practice} inquiry`, color: '#A78BFA' };
}

/** Compact relative time for inbox rows: "just now", "5m", "3h", "2d", then a date. */
export function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
